import _ from "lodash";

import {
	GET_SINGLE_USER_APPLICATION_ERROR,
	GET_SINGLE_USER_APPLICATION_QUOTE_ERROR,
	GET_SINGLE_USER_APPLICATION_QUOTE_SUCCESS,
	GET_SINGLE_USER_APPLICATION_REQUEST,
	GET_SINGLE_USER_APPLICATION_SUCCESS,
	GET_USER_APPLICATIONS_ERROR,
	GET_USER_APPLICATIONS_REQUEST,
	GET_USER_APPLICATIONS_SUCCESS,
	REMOVE_USER_APPLICATION,
	REMOVE_USER_APPLICATION_FROM_LIST,
	SAVE_APPLICATION_ERROR,
	SAVE_APPLICATION_QUOTE_ERROR,
	SAVE_APPLICATION_QUOTE_SUCCESS,
	SAVE_APPLICATION_REQUEST,
	SAVE_APPLICATION_WRITE_SUCCESS,
	STEP_CONTINUE,
	STEP_SUBMIT,
	UPDATE_APPLICATION_QUOTE_ERROR,
	UPDATE_APPLICATION_QUOTE_START,
	UPDATE_APPLICATION_QUOTE_SUCCESS,
	UPDATE_APPLICATION_REQUEST,
	UPDATE_APPLICATION_REQUEST_ERROR,
	UPDATE_APPLICATION_WRITE_SUCCESS,
} from "../actions/types";

const defaultState = {
	applications: [],
	isLoading: false,
	isApplicationSaving: false,
	isQuoteUpdating: false,
	isSingleApplicationLoading: false,
	isSubmitted: false,
	savedApplication: null,
	pendingWrites: 0,
	// Set when the active application's price could not be calculated (a quote that
	// failed, or a write that failed so no quote can follow). Distinct from
	// isQuoteUpdating: without it, "failed with no rate" is indistinguishable from
	// "still loading" and the price slot loops its loading message forever.
	quoteError: false,
	error: "",
};

/**
 * Filters an application by id
 * @param {string} id - The application id
 * @returns {Boolean} - True if app.id isn't equal to the ID passed in. False otherwise.
 */
export const filterAppFromList = (id) => (app) => app.id !== id;

/**
 * Merges a written application into state while RETAINING the prior
 * quote-derived rate/hasTiers - so a write does not flash a transient
 * zero/stale price before the latest quote lands.
 * @param {Object|null} previous - the existing savedApplication (may be null)
 * @param {Object} application - the freshly-written application data
 * @returns {Object} - merged application retaining prior rate/hasTiers
 */
const mergeApplicationRetainingRate = (previous, application) => {
	if (!previous) {
		return application;
	}

	return { ...application, rate: previous.rate, hasTiers: previous.hasTiers };
};

/**
 * User's application reducer logic.
 * @param {Object} state - The state for this reducer in Redux.
 * @param {Object} action - The action which details what to do & what state to update.
 * @returns {Object} - Newly created state object.
 */
export default (state = defaultState, action) => {
	switch (action.type) {
		case GET_USER_APPLICATIONS_REQUEST:
			return { ...state, isLoading: true };
		case GET_USER_APPLICATIONS_SUCCESS:
			return {
				...state,
				applications: action.payload,
				isLoading: false,
			};
		case GET_USER_APPLICATIONS_ERROR:
			return {
				...state,
				isLoading: false,
				error: action.payload,
			};
		case GET_SINGLE_USER_APPLICATION_REQUEST:
			return {
				...state,
				isSingleApplicationLoading: true,
				quoteError: false,
			};
		case GET_SINGLE_USER_APPLICATION_SUCCESS:
			// Application data only - the merged rate lands later via
			// GET_SINGLE_USER_APPLICATION_QUOTE_SUCCESS; product_totals/quoteItems
			// live in the react-query display cache (F3.1), not redux.
			//
			// isQuoteUpdating starts HERE because the resume thunk fires its background
			// quote immediately after this dispatch (getSingleUserApplication.js): the
			// window between the two is a quote in flight, and marking it keeps the
			// price slot on its loading message instead of flashing the failure state.
			return {
				...state,
				isSingleApplicationLoading: false,
				isQuoteUpdating: true,
				quoteError: false,
				savedApplication: action.payload.application,
			};
		case GET_SINGLE_USER_APPLICATION_ERROR:
			return {
				...state,
				isSingleApplicationLoading: false,
				error: action.payload,
			};
		case SAVE_APPLICATION_REQUEST:
		case UPDATE_APPLICATION_REQUEST: {
			// isApplicationSaving is a pending-writes counter (true until ALL
			// queued writes settle), not a per-response boolean.
			const pendingWrites = state.pendingWrites + 1;
			return { ...state, pendingWrites, isApplicationSaving: true };
		}
		case UPDATE_APPLICATION_WRITE_SUCCESS: {
			const pendingWrites = Math.max(0, state.pendingWrites - 1);
			const next = { ...state, pendingWrites, isApplicationSaving: pendingWrites > 0 };

			// A stale (superseded) write, OR a serialized write for a DIFFERENT
			// application than the one currently active (the user navigated away
			// while this PATCH was still queued), only advances the pending counter -
			// it must never merge onto (and corrupt) the active application.
			if (!action.payload?.application || action.payload.application.id !== state.savedApplication?.id) {
				return next;
			}

			return {
				...next,
				isQuoteUpdating: true,
				// A fresh quote follows this write, so any earlier failure is stale.
				quoteError: false,
				savedApplication: mergeApplicationRetainingRate(state.savedApplication, action.payload.application),
			};
		}
		case SAVE_APPLICATION_WRITE_SUCCESS: {
			const pendingWrites = Math.max(0, state.pendingWrites - 1);
			const next = { ...state, pendingWrites, isApplicationSaving: pendingWrites > 0 };

			// Stale (superseded) creation writes only advance the pending counter.
			if (!action.payload?.application) {
				return next;
			}

			// Creation replaces the active application unconditionally - a just-created
			// application IS the new active apply flow (the prior flow, if any, is
			// cleared via REMOVE_USER_APPLICATION when a fresh apply starts).
			return {
				...next,
				isQuoteUpdating: true,
				// A fresh quote follows this write, so any earlier failure is stale.
				quoteError: false,
				savedApplication: action.payload.application,
			};
		}
		case UPDATE_APPLICATION_QUOTE_START:
			// A rate-affecting update has started its write; the re-quote is coming.
			// Start the refresh state now (application-id-gated) so the price slot
			// covers the whole write+quote window, not just the quote leg. The quote
			// success/error clears it later under the existing generation + id gating.
			if (action.payload.applicationId !== state.savedApplication?.id) {
				return state;
			}
			return { ...state, isQuoteUpdating: true, quoteError: false };
		case SAVE_APPLICATION_ERROR:
		case UPDATE_APPLICATION_REQUEST_ERROR: {
			const pendingWrites = Math.max(0, state.pendingWrites - 1);
			// Clear the refresh state too: a failed write means no quote will land,
			// so an early-started isQuoteUpdating must not stick on the price slot.
			// For the same reason the price is UNAVAILABLE, not merely un-refreshed:
			// quoteError makes the slot say so instead of looping its loading message.
			// Harmless when a rate already exists - the slot only surfaces the failure
			// when there is no value to show.
			return {
				...state,
				pendingWrites,
				isApplicationSaving: pendingWrites > 0,
				isQuoteUpdating: false,
				quoteError: true,
				error: action.payload,
			};
		}
		case SAVE_APPLICATION_QUOTE_SUCCESS:
		case GET_SINGLE_USER_APPLICATION_QUOTE_SUCCESS:
			// Cross-application guard: only prime the quote of the currently-active
			// application. A quote for a non-active application leaves state COMPLETELY
			// unchanged (the active application may have its own quote in flight, so
			// isQuoteUpdating must NOT be cleared here).
			if (action.payload.applicationId !== state.savedApplication?.id) {
				return state;
			}
			// product_totals/quoteItems now live in the react-query display cache
			// (primed by runLatestGenerationQuote); redux keeps only the rate merge
			// onto savedApplication (retired in F3.2).
			return {
				...state,
				isQuoteUpdating: false,
				quoteError: false,
				savedApplication: { ...state.savedApplication, rate: action.payload.rate },
			};
		case UPDATE_APPLICATION_QUOTE_SUCCESS:
			if (action.payload.applicationId !== state.savedApplication?.id) {
				return state;
			}
			return {
				...state,
				isQuoteUpdating: false,
				quoteError: false,
				savedApplication: { ...state.savedApplication, rate: action.payload.rate, hasTiers: action.payload.hasTiers },
			};
		case SAVE_APPLICATION_QUOTE_ERROR:
		case UPDATE_APPLICATION_QUOTE_ERROR:
		case GET_SINGLE_USER_APPLICATION_QUOTE_ERROR:
			// Quote-only failure is non-fatal: clear the updating flag and retain
			// the prior quote data (no fatal error, no redirect). Only a matching
			// application's quote failure clears isQuoteUpdating - a failure for a
			// non-active application leaves state unchanged.
			//
			// quoteError records THAT it failed. With a prior rate retained the slot
			// keeps showing it; with no rate (e.g. a 400 "Rate cycle dates invalid" on
			// the first quote) the slot needs this to say the price is unavailable
			// rather than sit on "Refreshing Price..." indefinitely.
			if (action.payload?.applicationId !== state.savedApplication?.id) {
				return state;
			}
			return { ...state, isQuoteUpdating: false, quoteError: true };
		case REMOVE_USER_APPLICATION:
			// Clearing the active application also clears its quote-in-flight flag:
			// the departed flow's late quote is id-gated out above, so nothing else
			// would ever reset the flag and the next flow would inherit a stale
			// "Refreshing Price..." indicator.
			return {
				...state,
				savedApplication: null,
				isQuoteUpdating: false,
				quoteError: false,
			};
		case REMOVE_USER_APPLICATION_FROM_LIST:
			return {
				...state,
				applications: _.filter(state.applications, filterAppFromList(action.payload)),
			};
		case STEP_SUBMIT:
			return {
				...state,
				isSubmitted: true,
			};
		case STEP_CONTINUE:
			return {
				...state,
				isSubmitted: false,
			};
		default:
			return state;
	}
};
